import { List, Map } from 'immutable';

import { PerformanceKeys, SearchKeys, StatsActions, StatsStoreKeys } from './constants';
import * as D from './deps';
import TimeToMusicEvent from './models/TimeToMusicEvent';

const INITIAL_STATE = Map({
  [StatsStoreKeys.EVENTS]: List(),
  [StatsStoreKeys.SEARCH]: Map({
    [SearchKeys.CURRENT_SEARCH_SESSION_ID]: null,
    [SearchKeys.CURRENT_SEARCH_SEQUENCE_NUMBER]: 0,
  }),
  [StatsStoreKeys.BROWSE_SESSION_ID]: null,
  [StatsStoreKeys.PERFORMANCE]: Map({
    [PerformanceKeys.INITIAL_PLAYBACK]: false,
    [StatsStoreKeys.SKIP_TYPE]: null,
  }),
  [StatsStoreKeys.INITIALIZING]: false,
  [StatsStoreKeys.TTM_EVENTS]: Map(), // each key is a TimeToMusicEvent
  [StatsStoreKeys.PREVIOUS_PLAYED_HARNESS]: Map(),
});

// STATS REDUCER
export default D.createReducer(INITIAL_STATE, {
  [D.Constants.Analytics.ActionTypes.ADD_EVENT]: addEvent,
  [D.Constants.Analytics.ActionTypes.INCREMENT_SEARCH_SEQUENCE]: incrementSearchSequence,
  [D.Constants.Analytics.ActionTypes.RESET_SEARCH_SEQUENCE]: resetSearchSequence,
  [D.Constants.Analytics.ActionTypes.GENERATE_BROWSE_SESSION_ID]: generateBrowseSessionId,
  [D.Constants.Analytics.ActionTypes.INITIALIZE_STATS]: initializeStats,
  [D.Constants.Analytics.ActionTypes.CLEAR_STATS]: clearStats,
  [D.Constants.Analytics.ActionTypes.CLEAR_STATS_EVENTS]: clearStatsEvents,
  [D.Constants.Analytics.ActionTypes.UPDATE_LIVE_TRACKING]: updateLiveTracking,
  [D.Constants.Analytics.ActionTypes.CLEAR_LIVE_TRACKING]: clearLiveTracking,
  [D.Constants.UseCases.ActionTypes.SKIP_TRACK_FORWARD]: skipTrackForward,
  [StatsActions.TTM_START]: timeToMusicStart,
  [StatsActions.TTM_CANCEL]: timeToMusicCancel,
  [StatsActions.TTM_CLEAR]: timeToMusicClear,
  [StatsActions.TTM_TRANSITION]: timeToMusicTransitionFromAudioType,
  [StatsActions.UPDATE_PREVIOUS_PLAYED_HARNESS]: updatePreviousPlayedHarness,
});

// CASE FUNCTIONS

function addEvent(state, { payload }) {
  const events = payload.events.map(({ eventName, eventData }) => ({
    eventName,
    eventData,
  }));
  return state.update(StatsStoreKeys.EVENTS, stateEvents => stateEvents.concat(events));
}

function incrementSearchSequence(state) {
  return state.updateIn(
    [StatsStoreKeys.SEARCH, SearchKeys.CURRENT_SEARCH_SEQUENCE_NUMBER],
    sequence => sequence + 1,
  );
}

function resetSearchSequence(state) {
  return state.set(
    StatsStoreKeys.SEARCH,
    Map({
      [SearchKeys.CURRENT_SEARCH_SESSION_ID]: D.Utils.NumberUtils.getPseudoRandom(),
      [SearchKeys.CURRENT_SEARCH_SEQUENCE_NUMBER]: 0,
    }),
  );
}

function generateBrowseSessionId(state) {
  return state.set(StatsStoreKeys.BROWSE_SESSION_ID, D.Utils.NumberUtils.getPseudoRandom());
}

function initializeStats(state) {
  return state.set(StatsStoreKeys.INITIALIZING, true);
}

function clearStats() {
  return INITIAL_STATE;
}

function clearStatsEvents(state) {
  return state.set(StatsStoreKeys.EVENTS, List());
}

function updateLiveTracking(state, action) {
  return state.mergeIn([StatsStoreKeys.LIVE_TRACKING], action.data);
}

function clearLiveTracking(state) {
  if (state.get(StatsStoreKeys.INITIALIZING)) {
    return INITIAL_STATE;
  }
  return state.set(StatsStoreKeys.LIVE_TRACKING, INITIAL_STATE.get(StatsStoreKeys.LIVE_TRACKING));
}

function skipTrackForward(state, { payload }) {
  return state.setIn([StatsStoreKeys.PERFORMANCE, StatsStoreKeys.SKIP_TYPE], payload.skipType);
}

function timeToMusicStart(state, { payload }) {
  return state.setIn([StatsStoreKeys.TTM_EVENTS, payload.ttmAction], TimeToMusicEvent(payload));
}

function timeToMusicCancel(state, { payload }) {
  return state.deleteIn([StatsStoreKeys.TTM_EVENTS, payload.ttmAction]);
}

function timeToMusicClear(state) {
  return state.set(StatsStoreKeys.TTM_EVENTS, Map());
}

function timeToMusicTransitionFromAudioType(state, { payload: { audioType } }) {
  return state.update(StatsStoreKeys.TTM_EVENTS, events =>
    events.map((event, key) =>
      TimeToMusicEvent({
        ttmAction: key,
        timeStart: D.Utils.Time.getNow(),
        audioType,
      }),
    ),
  );
}

function updatePreviousPlayedHarness(state, { payload: { harnessType } }) {
  const currentTime = new Date().getTime();
  return state.setIn([StatsStoreKeys.PREVIOUS_PLAYED_HARNESS, harnessType], currentTime);
}
